import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const packages = [
  {
    id: 'starter',
    num: '01',
    name: 'GEO STARTER',
    tag: null,
    best: 'Businesses new to GEO wanting to establish AI visibility',
    price: 'From $1,200',
    priceNote: 'AUD / month',
    features: [
      { icon: 'ri-search-eye-line', text: 'AI Visibility Audit (ChatGPT, Gemini, Perplexity)' },
      { icon: 'ri-tools-line', text: 'Technical AI crawlability fix' },
      { icon: 'ri-code-s-slash-line', text: 'Schema markup implementation' },
      { icon: 'ri-article-line', text: '5 AI-optimised content pieces per month' },
      { icon: 'ri-star-line', text: 'Review generation strategy' },
      { icon: 'ri-bar-chart-2-line', text: 'Monthly AI visibility report' },
    ],
    cta: 'Get Started',
    highlight: false,
  },
  {
    id: 'dominate',
    num: '02',
    name: 'GEO DOMINATE',
    tag: '★ Most Popular',
    best: 'Competitive local markets where first-mover advantage is critical',
    price: 'From $2,500',
    priceNote: 'AUD / month',
    features: [
      { icon: 'ri-check-double-line', text: 'Everything in GEO Starter' },
      { icon: 'ri-quill-pen-line', text: '10 AI-optimised content pieces per month' },
      { icon: 'ri-bar-chart-grouped-line', text: 'Full competitor AI share-of-voice tracking' },
      { icon: 'ri-links-line', text: 'Third-party authority and mention building' },
      { icon: 'ri-megaphone-line', text: 'Local PR and citation strategy' },
      { icon: 'ri-calendar-check-line', text: 'Quarterly strategy review and roadmap' },
      { icon: 'ri-flashlight-line', text: 'Priority content turnaround' },
    ],
    cta: 'Get Started',
    highlight: true,
  },
  {
    id: 'fullstack',
    num: '03',
    name: 'GEO + SEO FULL STACK',
    tag: null,
    best: 'Businesses who want total search dominance — traditional and AI',
    price: 'Custom Pricing',
    priceNote: '',
    features: [
      { icon: 'ri-check-double-line', text: 'Everything in GEO Dominate' },
      { icon: 'ri-map-pin-2-line', text: 'Full Local SEO campaign running in parallel' },
      { icon: 'ri-git-merge-line', text: 'Integrated content strategy across SEO + GEO' },
      { icon: 'ri-google-line', text: 'Google Business Profile management' },
      { icon: 'ri-money-dollar-circle-line', text: 'Paid search consultation' },
      { icon: 'ri-phone-line', text: 'Weekly performance check-ins' },
    ],
    cta: 'Get Started',
    highlight: false,
  },
];

export default function GeoPricingSection() {
  const groupRef = useScrollRevealGroup();
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <section id="geo-pricing" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-ds-red/5 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Investment</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              GEO / AEO
              <br />
              <span className="text-ds-red neon-text-red">PACKAGES.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-sm reveal">
              GEO is an ongoing discipline — AI search is evolving constantly, and maintaining visibility requires continuous optimisation. Here&apos;s how we structure the investment.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-6 reveal">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onMouseEnter={() => setHovered(pkg.id)}
              onMouseLeave={() => setHovered(null)}
              className={`relative flex flex-col transition-all duration-300 cursor-default ${pkg.highlight ? 'border border-ds-red bg-ds-bg-2' : `border bg-ds-bg-2 ${hovered === pkg.id ? 'border-white/20' : 'border-ds-border'}`}`}
              style={pkg.highlight ? { boxShadow: '0 0 40px rgba(180,255,57,0.12), inset 0 0 40px rgba(180,255,57,0.03)' } : {}}
            >
              {pkg.tag && (
                <div className="absolute -top-px left-0 right-0 flex justify-center">
                  <span className="bg-ds-red text-ds-bg font-display text-xs tracking-widest px-4 py-1">{pkg.tag}</span>
                </div>
              )}
              <div className={`p-8 flex flex-col flex-1 ${pkg.tag ? 'pt-10' : ''}`}>
                <div className="mb-6">
                  <span className="font-display text-ds-red text-xs tracking-[0.3em]">PACKAGE {pkg.num}</span>
                  <h3 className="font-display text-xl text-white mt-1 leading-tight tracking-wider">{pkg.name}</h3>
                  <p className="font-body text-white/40 text-xs mt-2 leading-relaxed">Best for: {pkg.best}</p>
                </div>
                <div className={`h-px mb-6 ${pkg.highlight ? 'bg-ds-red/40' : 'bg-ds-border'}`} />
                <ul className="space-y-3 flex-1 mb-8">
                  {pkg.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${f.icon} ${pkg.highlight ? 'text-ds-red' : 'text-white/40'} text-sm`} />
                      </div>
                      <span className="font-body text-white/70 text-sm leading-snug">{f.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`font-display text-3xl ${pkg.highlight ? 'text-white' : 'text-white/90'}`}>{pkg.price}</span>
                    {pkg.priceNote && <span className="font-body text-white/40 text-xs">{pkg.priceNote}</span>}
                  </div>
                  {!pkg.priceNote && (
                    <p className="font-body text-white/40 text-xs mt-1">Tailored to your requirements</p>
                  )}
                </div>
                <a
                  href="#geo-contact"
                  className={`whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-xs tracking-widest transition-all duration-200 ${pkg.highlight ? 'bg-ds-red text-white hover:bg-ds-red/90 border border-ds-red' : 'bg-transparent text-white border border-ds-border hover:border-white/40'}`}
                  style={pkg.highlight ? { boxShadow: '0 0 20px rgba(180,255,57,0.3)' } : {}}
                >
                  {pkg.cta}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <p className="font-body text-white/30 text-xs text-center mt-6 reveal">
          All prices in AUD, excluding GST. Minimum 3-month commitment on all GEO packages. Custom quotes available for multi-location businesses.
        </p>

        {/* Final CTA */}
        <div id="geo-contact" className="mt-16 border border-ds-red/30 bg-ds-bg p-12 text-center reveal" style={{ boxShadow: '0 0 60px rgba(180,255,57,0.08)' }}>
          <span className="section-label mb-4 inline-block">Ready to Own AI Search?</span>
          <h3 className="font-display text-4xl md:text-5xl text-white leading-none mb-4">
            FIND OUT IF YOUR BUSINESS IS
            <br />
            <span className="text-ds-red neon-text-red">INVISIBLE TO AI RIGHT NOW.</span>
          </h3>
          <p className="font-body text-white/55 text-sm leading-relaxed max-w-2xl mx-auto mb-3">
            We&apos;ll run a free AI Visibility Audit — testing your business across ChatGPT, Google Gemini, and Perplexity. We&apos;ll show you exactly where you appear, where your competitors are showing up instead, and what it would take to change that.
          </p>
          <p className="font-body text-white/40 text-xs mb-8 tracking-wider">
            Takes 30 minutes. Could change everything about how customers find you.
          </p>
          <a href="/#contact" className="btn-red inline-flex whitespace-nowrap">
            <span>Get Your Free AI Visibility Audit</span>
            <i className="ri-arrow-right-line" />
          </a>
          <p className="font-body text-white/30 text-xs mt-5">
            No lock-in. No pressure. Just the truth about where you stand in the new search landscape.
          </p>
          <div className="mt-10 pt-8 border-t border-ds-border/40">
            <p className="font-display text-white/20 text-xs tracking-[0.4em] uppercase">
              Darkside Digital — The Future of Search is AI. Own It.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
